/** Theme table for the sign-in particle field.
 *
 *  Ported from the CinchAI (open-webui) auth page so both products share the
 *  same seasonal behaviour. Seven themes light up on their own dates; the rest
 *  are keyboard easter eggs — type the code anywhere on the sign-in screen
 *  (outside a text field) and the field restarts in that theme.
 *
 *  Lives in .ts rather than .tsx on purpose: the palettes below are raw hex,
 *  which the design-system lint rule bans in .tsx. These are canvas artwork
 *  colours (flags, starfighters, confetti), not UI chrome — they have no
 *  semantic token equivalent.
 */

export type ThemeName =
  | "default"
  | "newyear"
  | "vday"
  | "stpatricks"
  | "spring"
  | "july4"
  | "halloween"
  | "christmas"
  | "matrix"
  | "snow"
  | "og"
  | "starwars"
  | "cinchlaunch"
  | "spark";

/** Cinch product-tile colours, used by the launch-day confetti + tiles. */
export const CINCH_CONFETTI_COLORS = ["#d98035", "#63b680", "#f4bb4d", "#397cb9"];

/** Weighted 3:2 red/blue so July 4 bursts read as a flag, not a gradient. */
export const JULY4_COLORS = ["#BF0A30", "#BF0A30", "#002868", "#002868", "#BF0A30"];

/** Date-driven themes. Windows are inclusive and deliberately short — the
 *  sign-in screen should feel like it noticed the date, not like a costume. */
export function getDateTheme(now: Date = new Date()): ThemeName {
  const m = now.getMonth() + 1;
  const d = now.getDate();
  if (m === 1 && d <= 7) return "newyear";
  if (m === 2 && d >= 10 && d <= 17) return "vday";
  if (m === 3 && d >= 13 && d <= 20) return "stpatricks";
  if (m === 3 && d >= 21 && d <= 28) return "spring"; // Spring rain
  if (m === 4 && d === 13) return "cinchlaunch"; // Cinch product launch day
  if (m === 5 && d === 4) return "starwars"; // May the Fourth
  if (m === 7 && d >= 1 && d <= 7) return "july4";
  if (m === 10 && d >= 25 && d <= 31) return "halloween";
  if (m === 12 && d >= 20 && d <= 26) return "christmas";
  return "default";
}

/** Type one of these keys on the sign-in screen to force a theme. Matched
 *  against the last 12 keystrokes, so a trailing match is enough. */
export const EASTER_EGGS: Record<string, ThemeName> = {
  newyear: "newyear",
  vday: "vday",
  patty: "stpatricks",
  spring: "spring",
  july4: "july4",
  halloween: "halloween",
  christmas: "christmas",
  matrix: "matrix",
  snow: "snow",
  og: "og",
  starwars: "starwars",
  launch: "cinchlaunch",
  spark: "spark",
};

export const KONAMI_BUFFER_LEN = 12;

/** Human labels — used by the Design System preview and nothing else. */
export const THEME_LABELS: Record<ThemeName, string> = {
  default: "Default plexus",
  newyear: "New Year",
  vday: "Valentine's",
  stpatricks: "St. Patrick's",
  spring: "Spring rain",
  july4: "July 4th",
  halloween: "Halloween",
  christmas: "Christmas",
  matrix: "Matrix",
  snow: "Snow",
  og: "OG pattern",
  starwars: "Star Wars",
  cinchlaunch: "Cinch launch",
  spark: "Spark torus",
};

/** What each theme does when you click the backdrop. */
export const THEME_CLICK_HINTS: Partial<Record<ThemeName, string>> = {
  default: "Big bang — blows every dot away from the cursor",
  newyear: "Confetti cannon",
  july4: "Firework burst",
  spring: "Launches the flowerbed and splashes the rain",
  cinchlaunch: "Snaps the tiles into a grid for ~3s",
  starwars: "Blows up the Death Star, then jumps to hyperspace",
};

/** Page background a theme takes over, if any. Rendered by the React wrapper
 *  as its own layer — the engine never reaches out and restyles the page. */
export function themeBackground(theme: ThemeName): Record<string, string> | undefined {
  if (theme === "og") {
    return {
      backgroundImage: "url(/brand/sign-in-pattern.jpg)",
      backgroundSize: "auto",
      backgroundRepeat: "repeat",
    };
  }
  if (theme === "spark") {
    return { background: "linear-gradient(135deg, #060814 0%, #0a0f1e 100%)" };
  }
  return undefined;
}

/** Dot fill + connection-line prefix (callers append `${alpha})`). */
export function getThemeColors(theme: ThemeName, dark: boolean): { dot: string; line: string } {
  switch (theme) {
    case "vday":
      return { dot: "rgba(255,80,130,0.9)", line: "rgba(255,80,130," };
    case "stpatricks":
      return { dot: "rgba(50,185,75,0.9)", line: "rgba(50,185,75," };
    case "halloween":
      return { dot: "rgba(255,100,0,0.9)", line: "rgba(148,0,211," };
    case "christmas":
      return { dot: "rgba(215,245,255,0.9)", line: "rgba(220,50,50," };
    case "spring":
      return { dot: "rgba(100,180,255,0.8)", line: "rgba(100,180,255," };
    case "starwars":
      return { dot: "rgba(200,200,210,0.9)", line: "rgba(0,120,255," };
    case "july4":
      return { dot: "rgba(255,255,255,0.95)", line: "rgba(255,0,0," };
    case "snow":
      return { dot: "rgba(200,230,255,0.85)", line: "rgba(200,230,255," };
    case "cinchlaunch":
      return { dot: "rgba(54,98,227,0.9)", line: "rgba(140,150,165," };
    case "spark":
      return { dot: "rgba(255,245,180,0.9)", line: "rgba(160,210,255," };
    default:
      return {
        dot: dark ? "rgba(120,180,255,0.7)" : "rgba(30,100,200,0.5)",
        line: dark ? "rgba(120,180,255," : "rgba(30,100,200,",
      };
  }
}
